'use client';

import React, { useEffect } from 'react';
import { cm } from 'src/utils/classNames';
import { FloatingPortal } from '@floating-ui/react';
import usePopupOverlay from 'src/components/Popups/PopupOverlay/hooks/usePopupOverlay';
import { Z_INDEX } from 'src/constants/zIndex';

export type FloatingPopoverProps = {
	setFloating?: (node: HTMLDivElement) => void;
	floatingStyles: React.CSSProperties;
	getFloatingProps: () => Record<string, any>;
};

type PopoverWrapperProps = {
	portalId?: string;
	children: React.ReactNode;
	/**
	 * Applies the specified classnames to the popover content wrapper.
	 * Note, if you need to include a `z-index`, use the `zIndex` prop.
	 */
	className?: string;
	/**
	 * Applies the specified styles to the popover content wrapper.
	 * Note, if you need to include a `z-index`, use the `zIndex` prop.
	 */
	style?: React.CSSProperties;
	/**
	 * Applies a `z-index` of the specified value to the popover and a z-index of
	 * the specified number - 1 to the popover overlay.
	 */
	zIndex?: number;
	noBorder?: boolean;
	useTargetWidth?: boolean;
};

const PopoverWrapper = React.forwardRef(
	(
		{ zIndex = Z_INDEX.popup, ...props }: PopoverWrapperProps,
		ref: React.ForwardedRef<HTMLDivElement>
	) => {
		const [targetWidth, setTargetWidth] = React.useState<number | null>(null);

		usePopupOverlay({ zIndex });

		useEffect(() => {
			if (props.useTargetWidth) {
				if (!props.portalId) {
					console.warn(
						"PopoverWrapper: 'id' prop is required when 'useTargetWidth' is true"
					);
					return;
				}

				const targetElement = document.getElementById(props.portalId);
				if (targetElement) {
					setTargetWidth(targetElement.offsetWidth);
				}
			}
		}, [props.portalId, props.useTargetWidth]);

		if (props.useTargetWidth && !targetWidth) {
			return null;
		}

		return (
			<FloatingPortal id={`${props.portalId}_portal`}>
				<div
					ref={ref}
					className={cm(
						'overflow-auto bg-container-bg align-middle',
						'rounded-sm text-text-default',
						!props.noBorder && 'border border-stroke-divider',
						props.className
					)}
					style={{
						width: targetWidth ? `${targetWidth}px` : undefined,
						...props.style,
						zIndex,
					}}
				>
					{props.children}
				</div>
			</FloatingPortal>
		);
	}
);

PopoverWrapper.displayName = 'PopoverWrapper';

export default PopoverWrapper;
